import { type FC, useState, memo, useRef } from "react";
import { Button } from "@/components/ui/button";
import Calendar from "@/components/ui/calendar";
import { DateRange as DayPickerRange } from "react-day-picker";
import { simpleDate, areRangesEqual } from "@/utils/datetime";
import Text from "@/components/ui/text";
import { displaySelectedDates } from "@/components/controls/displaySelectedDates";

export interface DateRangePickerLabels {
  cancel: string;
  apply: string;
}

export interface DateRangePickerProps {
  labels: DateRangePickerLabels;
  onUpdate: (values: { range: DateRange }) => void;
  onCancel: () => void;
  initialFromDate?: Date;
  initialToDate?: Date;
  alwaysApply?: boolean;
}

export interface DateRange {
  fromDate: Date | string;
  toDate?: Date | string;
}

const RangeCalendar = memo(
  ({
    selected,
    onSelect
  }: {
    selected: DayPickerRange | undefined;
    onSelect: (range: DayPickerRange | undefined) => void;
  }) => (
    <Calendar
      mode="range"
      selected={selected}
      onSelect={onSelect}
      defaultMonth={selected?.to || selected?.from}
    />
  )
);

const DateDisplay = memo(({ range }: { range: DateRange | null }) => (
  <Text variant="bodySecondary" className="mx-4">
    {displaySelectedDates(range, simpleDate)}
  </Text>
));

// Default custom range is set to the previous 7 days
const getDefaultDatesRange = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 6);

  return {
    from: sevenDaysAgo,
    to: today
  };
};

export const DateRangePicker: FC<DateRangePickerProps> = ({
  labels,
  initialFromDate,
  initialToDate,
  onUpdate,
  onCancel,
  alwaysApply = false
}): JSX.Element => {
  const defaultDates = getDefaultDatesRange();

  const [selected, setSelected] = useState<DayPickerRange | undefined>({
    from: initialFromDate || defaultDates.from,
    to: initialToDate || defaultDates.to
  });

  const openedRangeRef = useRef(selected);

  const handleApply = () => {
    if (
      selected?.from &&
      (alwaysApply
        ? true
        : !areRangesEqual(
            { fromDate: selected.from, toDate: selected.to },
            openedRangeRef.current
              ? {
                  fromDate: openedRangeRef.current.from!,
                  toDate: openedRangeRef.current.to
                }
              : undefined
          ))
    ) {
      onUpdate({
        range: {
          fromDate: selected.from,
          toDate: selected.to || selected.from
        }
      });
    }
  };

  return (
    <div className="w-fit rounded-lg flex flex-col">
      <RangeCalendar selected={selected} onSelect={setSelected} />
      <DateDisplay
        range={
          selected?.from
            ? { fromDate: selected.from, toDate: selected.to }
            : null
        }
      />
      <div className="flex justify-end gap-2 py-2 pr-4">
        <Button onClick={onCancel} variant="secondary">
          {labels.cancel}
        </Button>
        <Button onClick={handleApply}>{labels.apply}</Button>
      </div>
    </div>
  );
};

export default memo(DateRangePicker);
